import { CarTypeOrder } from "@eurodrive/utils"
import { transformManufacturers } from "@eurodrive/utils/transformMetadataFilters"
import { SearchPageMetadata } from "@eurodrive/utils/metadataTypes"
import { CarManufacturerDto, SearchMetadataResponse } from "api"
import { useMemo } from "react"

export function useManufacturerOptions(
  metadata: SearchPageMetadata,
  filteredManufacturers: CarManufacturerDto[],
  selectedManufacturer: string | null,
) {
  // Collect all manufacturers from all metadata to handle cases where
  // the selected manufacturer is not in the current filtered list
  const allManufacturers = useMemo(() => {
    const all: CarManufacturerDto[] = []
    const addManufacturers = (meta: SearchMetadataResponse) => {
      if (meta.manufacturers) {
        all.push(...meta.manufacturers)
      }
    }

    // Add from every category of every deal-type context
    CarTypeOrder.forEach((carType) => {
      addManufacturers(metadata.sale.new[carType])
      addManufacturers(metadata.sale.used[carType])
      addManufacturers(metadata.leasing[carType])
    })

    return all
  }, [metadata])

  const transformedManufacturers = useMemo(() => {
    const transformed = transformManufacturers(filteredManufacturers)

    // If selected manufacturer is not in the list, try to find it in allManufacturers
    // and add it to the list so the UI can display it properly
    if (selectedManufacturer) {
      const exists = transformed.some((m) => m.value === selectedManufacturer)
      if (!exists) {
        const manufacturer = allManufacturers.find(
          (m) => m.code.toString() === selectedManufacturer,
        )
        if (manufacturer) {
          transformed.push({
            value: manufacturer.code.toString(),
            label: manufacturer.name,
            models: manufacturer.models.map((model) => ({
              value: model.modelCode.toString(),
              label: model.name,
            })),
          })
        }
      }
    }

    return transformed
  }, [filteredManufacturers, selectedManufacturer, allManufacturers])

  return transformedManufacturers
}
